import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import AICatalogFormComp from "./AICatalog/Form";
import {
  useProjects,
  validateSelectedProjects,
} from "../contexts/ProjectContext";
import { apiKeysApi } from "../services/api";
import { llmApi } from "../services/rag-service";
import { isUserAdmin } from "../utils/auth-helpers";

const privateProvider = {
  id: "private",
  name: "private",
  supports_chat: true,
  supports_embedding: true,
  supports_generation: true,
  supports_multimodal: true,
};

/**
 * Thin compatibility wrapper for registering an Omnimizer deployment as a RAG
 * model. The shared AICatalog form owns the UI and validation surface.
 */
const RegisterModelForRagDialog = ({
  open,
  onClose,
  deployment,
  availableApiKeys,
  endpointUrl,
}) => {
  const { t } = useTranslation();
  const { selectedProjects, availableProjects } = useProjects();
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const canShowPublicAccessToggle = isUserAdmin();
  const isFailedDeployment =
    String(deployment?.status || "").toUpperCase() === "FAILED";

  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects),
    [selectedProjects],
  );
  const safeAvailableProjects = useMemo(
    () => (Array.isArray(availableProjects) ? availableProjects : []),
    [availableProjects],
  );

  const projectOptions = useMemo(() => {
    if (safeSelectedProjects.includes("all")) return safeAvailableProjects;

    return safeAvailableProjects.filter((project) =>
      safeSelectedProjects.includes(project.id),
    );
  }, [safeAvailableProjects, safeSelectedProjects]);

  const defaultProjectId = useMemo(
    () =>
      safeSelectedProjects.length === 1 && safeSelectedProjects[0] !== "all"
        ? safeSelectedProjects[0]
        : "",
    [safeSelectedProjects],
  );

  useEffect(() => {
    if (!open || !deployment) return;

    if (availableApiKeys && availableApiKeys.length > 0) {
      setApiKeys(availableApiKeys.filter((key) => key.is_active));
      return;
    }

    const fetchApiKeys = async () => {
      if (!deployment.model_id) return;

      setApiKeysLoading(true);
      try {
        const response = await apiKeysApi.listApiKeys(
          deployment.model_id,
          deployment.user_id,
        );
        setApiKeys((response.data || []).filter((key) => key.is_active));
      } catch (error) {
        console.error("Error fetching API keys:", error);
        toast.error(t("registerModelForRag.failedToLoadApiKeys"));
      } finally {
        setApiKeysLoading(false);
      }
    };

    fetchApiKeys();
  }, [open, deployment, availableApiKeys, t]);

  const apiKeyOptions = useMemo(
    () =>
      apiKeys.map((key) => ({
        value: key.id || "",
        label:
          key.description ||
          t("registerModelForRag.apiKeyCreated", {
            date: new Date(key.created_at).toLocaleDateString(),
          }),
      })),
    [apiKeys, t],
  );

  const initialFormData = useMemo(
    () => ({
      name: deployment?.name ? `${deployment.name} (RAG)` : "",
      provider: "private",
      model: deployment?.name || "",
      temperature: 0.7,
      max_tokens: 0,
      endpoint: isFailedDeployment
        ? ""
        : endpointUrl || deployment?.endpoint_url || "",
      is_chat_model: true,
      is_embedding_model: false,
      embedding_dimension: 1536,
      is_multimodal: false,
      api_key: "",
      auth_header_type: "x-omnimize-api-key",
      is_default: false,
      project_id: defaultProjectId,
      is_public: false,
    }),
    [defaultProjectId, deployment, endpointUrl, isFailedDeployment],
  );

  const getDecryptedApiKey = async (apiKeyId) => {
    const selectedApiKey = apiKeys.find(
      (key) => String(key.id) === String(apiKeyId),
    );
    if (!selectedApiKey) return apiKeyId;

    if (selectedApiKey.key && selectedApiKey.key.startsWith("omni_")) {
      return selectedApiKey.key;
    }

    try {
      const response = await apiKeysApi.getApiKey(
        deployment.model_id,
        selectedApiKey.id,
      );
      return response.data.key;
    } catch (error) {
      console.error("Error fetching decrypted API key:", error);
      throw new Error(t("registerModelForRag.failedToRetrieveApiKey"));
    }
  };

  const transformRequestData = async (requestData, formData) => {
    const decryptedApiKey = await getDecryptedApiKey(formData.api_key);
    const isPublicRegistration =
      canShowPublicAccessToggle && formData.is_public;
    const scopedRequestData = { ...requestData };

    if (isPublicRegistration) {
      delete scopedRequestData.project_id;
    } else {
      scopedRequestData.project_id = formData.project_id;
    }

    return {
      ...scopedRequestData,
      provider: "private",
      endpoint: formData.endpoint,
      auth_header_type: "x-omnimize-api-key",
      is_active: true,
      is_public: isPublicRegistration,
      external_id: deployment?.model_id || deployment?.id,
      extra_config: {
        ...requestData.extra_config,
        api_key: decryptedApiKey,
      },
    };
  };

  const handleSubmitRequest = async (requestData) => {
    if (requestData.is_public) {
      await llmApi.createAdminLLMConfig(requestData);
    } else {
      await llmApi.createUserLLMConfig(requestData);
    }
  };

  return (
    <AICatalogFormComp
      selectedModelId={null}
      isPublic={false}
      open={open}
      onClose={onClose}
      onSuccess={() => undefined}
      title={t("registerModelForRag.title")}
      initialFormData={initialFormData}
      providersOverride={[privateProvider]}
      projectsOverride={projectOptions}
      providerDisabled
      authHeaderDisabled
      projectPlaceholder={t("aiCatalog.selectProject")}
      apiKeyOptions={apiKeyOptions}
      apiKeysLoading={apiKeysLoading}
      noApiKeysMessage={t("registerModelForRag.createApiKeyFirst")}
      apiKeyInputMode="select"
      allowedModelTypes={[
        "is_chat_model",
        "is_multimodal",
        "is_embedding_model",
        "is_tts_model",
        "is_stt_model",
        "is_image_generation_model",
      ]}
      showPublicAccessToggle={canShowPublicAccessToggle}
      submitLabel={t("modelRegistration.registerModel")}
      submittingLabel={t("modelRegistration.registering")}
      successMessage={t("registerModelForRag.registerSuccess")}
      validateEndpointUrl={false}
      skipDuplicateCheck
      transformRequestData={transformRequestData}
      onSubmitRequest={handleSubmitRequest}
    />
  );
};

export default RegisterModelForRagDialog;
